import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import Table from '../components/table/Table';
import { fetchProvizeZamestnanec } from '../lib/crmClient';
import { getEmployeeByEmail, getEmployeeIdByEmail, TECHNICI } from '../lib/technicians';

const resolvedVfs =
  (pdfFonts && pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) ||
  (pdfFonts && pdfFonts.vfs) ||
  pdfFonts;

if (resolvedVfs) {
  pdfMake.vfs = resolvedVfs;
}

pdfMake.fonts = {
  Roboto: {
    normal: 'Roboto-Regular.ttf',
    bold: 'Roboto-Medium.ttf',
    italics: 'Roboto-Italic.ttf',
    bolditalics: 'Roboto-MediumItalic.ttf',
  },
};

const formatMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '-';
  const roundedUp = Math.ceil(n);
  return `${roundedUp.toLocaleString('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}\u00A0Kč`;
};

const formatDate = (value) => {
  if (!value) return '-';
  const raw = String(value).slice(0, 10);
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return String(value);
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
};

const monthLabel = (monthNumber) => {
  const n = Number(monthNumber);
  if (!Number.isFinite(n) || n < 1 || n > 12) return '-';
  return new Intl.DateTimeFormat('cs-CZ', { month: 'long' }).format(new Date(2020, n - 1, 1));
};

const formatMoneyInteger = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.ceil(n);
};

const Provize = ({ email, canViewAllJobs }) => {
  const location = useLocation();
  const restoredState = location.state && location.state.provizeState ? location.state.provizeState : null;
  const canViewAll = Boolean(canViewAllJobs);
  const ownEmployee = useMemo(() => getEmployeeByEmail(email), [email]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(() => restoredState?.selectedEmployeeId || getEmployeeIdByEmail(email));
  const [periodKey, setPeriodKey] = useState(() => restoredState?.periodKey || 'previous');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const ownId = getEmployeeIdByEmail(email);
    if (!canViewAll) {
      setSelectedEmployeeId(ownId);
      return;
    }
    if (!selectedEmployeeId) {
      setSelectedEmployeeId(ownId);
    }
  }, [canViewAll, email, selectedEmployeeId]);

  const periodOptions = useMemo(() => {
    const base = new Date();
    const current = { year: base.getFullYear(), month: base.getMonth() + 1 };
    const prev = new Date(base.getFullYear(), base.getMonth() - 1, 1);
    const previous = { year: prev.getFullYear(), month: prev.getMonth() + 1 };
    return { current, previous };
  }, []);

  const selectedPeriod = periodKey === 'current' ? periodOptions.current : periodOptions.previous;
  const provizeReturnTo = useMemo(() => ({
    pathname: '/provize',
    state: {
      provizeState: {
        periodKey,
        selectedEmployeeId,
      },
    },
  }), [periodKey, selectedEmployeeId]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    const run = async () => {
      setLoading(true);
      setError('');
      try {
        const employeeIdToLoad = canViewAll ? selectedEmployeeId : getEmployeeIdByEmail(email);
        if (!employeeIdToLoad) {
          throw new Error('Pro přihlášený e-mail se nepodařilo najít ZamestnanecId v offline číselníku.');
        }

        const data = await fetchProvizeZamestnanec({
          zamestnanecId: employeeIdToLoad,
          year: selectedPeriod.year,
          month: selectedPeriod.month,
          detail: 1,
          email,
          signal: controller.signal,
        });

        if (!cancelled) {
          setRows(data);
        }
      } catch (e) {
        if (controller.signal.aborted) return;
        if (!cancelled) {
          setRows([]);
          setError(e instanceof Error ? e.message : 'Načtení provizí selhalo');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    run();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [email, canViewAll, selectedEmployeeId, selectedPeriod]);

  const summary = useMemo(() => {
    if (!rows.length) {
      return {
        provizeZam: 0,
        cenaZakazky: 0,
        doprava: 0,
        dph: 0,
        dan: 0,
      };
    }

    return rows.reduce(
      (acc, row) => ({
        provizeZam: acc.provizeZam + (Number(row.Provize_ZAM) || 0),
        cenaZakazky: acc.cenaZakazky + (Number(row.CenaZakazky) || 0),
        doprava: acc.doprava + (Number(row.CenaDoprava) || 0),
        dph: acc.dph + (Number(row.DPH) || 0),
        dan: acc.dan + (Number(row.Dan) || 0),
      }),
      { provizeZam: 0, cenaZakazky: 0, doprava: 0, dph: 0, dan: 0 }
    );
  }, [rows]);

  const columns = [
    { key: 'Datum', numeric: false, className: 'provize-col-date' },
    { key: 'Zákazník', numeric: false, className: 'provize-col-customer' },
    { key: 'Firma', numeric: false, className: 'provize-col-company provize-col-optional' },
    { key: 'Škůdce', numeric: false, className: 'provize-col-pest provize-col-optional' },
    { key: 'Cena', numeric: true, className: 'provize-col-money' },
    { key: 'Doprava', numeric: true, className: 'provize-col-money provize-col-optional-small' },
    { key: 'DPH', numeric: true, className: 'provize-col-money provize-col-optional' },
    { key: 'Daň', numeric: true, className: 'provize-col-money provize-col-optional' },
    { key: 'Provize', numeric: true, className: 'provize-col-money' },
    { key: 'Akce', numeric: false, className: 'provize-col-actions' },
  ];
  const tableHead = columns.map((item) => item.key);
  const pdfColumns = columns.filter((item) => item.key !== 'Akce');

  const selectedEmployeeName = useMemo(() => {
    if (canViewAll) {
      const selected = TECHNICI.find((item) => String(item.id) === String(selectedEmployeeId));
      return selected ? selected.fullName : 'Neznámý zaměstnanec';
    }
    return ownEmployee ? ownEmployee.fullName : 'Neznámý zaměstnanec';
  }, [canViewAll, selectedEmployeeId, ownEmployee]);

  const handleExportPdf = () => {
    const periodText = `${monthLabel(selectedPeriod.month)} ${selectedPeriod.year}`;
    const generatedAt = new Date().toLocaleString('cs-CZ');
    const toPdfMoney = (value) => `${formatMoneyInteger(value).toLocaleString('cs-CZ')}\u00A0Kč`;
    const reportTitle = `Provize - ${selectedEmployeeName} - ${periodText}`;

    const summaryLabels = ['Provize', 'Cena', 'Doprava', 'DPH', 'Daň'];
    const summaryValues = [
      toPdfMoney(summary.provizeZam),
      toPdfMoney(summary.cenaZakazky),
      toPdfMoney(summary.doprava),
      toPdfMoney(summary.dph),
      toPdfMoney(summary.dan),
    ];

    const tableRows = rows.map((item) => [
      formatDate(item.Datum),
      item.Zakaznik || '-',
      item.Firma || '-',
      item.Skudce || '-',
      toPdfMoney(item.CenaZakazky),
      toPdfMoney(item.CenaDoprava),
      toPdfMoney(item.DPH),
      toPdfMoney(item.Dan),
      toPdfMoney(item.Provize_ZAM),
    ]);

    const docDefinition = {
      pageSize: 'A4',
      pageOrientation: 'landscape',
      pageMargins: [10, 8, 10, 10],
      defaultStyle: {
        font: 'Roboto',
        fontSize: 9,
      },
      footer: (currentPage, pageCount) => {
        if (currentPage !== pageCount) {
          return { text: '' };
        }
        return {
          columns: [
            { text: '' },
            {
              text: `Vygenerováno: ${generatedAt}`,
              alignment: 'right',
              margin: [0, 0, 10, 4],
              fontSize: 8,
              color: '#616161',
            },
          ],
        };
      },
      content: [
        {
          table: {
            widths: ['*'],
            body: [[{ text: reportTitle, style: 'titleBar' }]],
          },
          layout: 'noBorders',
          margin: [0, 0, 0, 3],
        },
        {
          table: {
            widths: ['*', '*', '*', '*', '*'],
            body: [
              summaryLabels.map((label) => ({ text: label, style: 'summaryLabelCenter' })),
              summaryValues.map((value) => ({ text: value, style: 'summaryValueCenter' })),
            ],
          },
          layout: {
            fillColor: (rowIndex) => (rowIndex === 0 ? '#f7f7f7' : null),
            hLineColor: () => '#dddddd',
            vLineColor: () => '#dddddd',
          },
          margin: [0, 0, 0, 6],
        },
        {
          table: {
            headerRows: 1,
            widths: ['auto', '*', 'auto', '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
            body: [
              pdfColumns.map((item) => ({ text: item.key, style: item.numeric ? 'thRight' : 'thLeft' })),
              ...tableRows.map((row) => ([
                { text: row[0], style: 'tdLeft' },
                { text: row[1], style: 'tdLeft' },
                { text: row[2], style: 'tdLeft' },
                { text: row[3], style: 'tdLeft' },
                { text: row[4], style: 'tdRight' },
                { text: row[5], style: 'tdRight' },
                { text: row[6], style: 'tdRight' },
                { text: row[7], style: 'tdRight' },
                { text: row[8], style: 'tdRight' },
              ])),
            ],
          },
          layout: {
            fillColor: (rowIndex, _node) => {
              if (rowIndex === 0) return '#d68102';
              return rowIndex % 2 === 0 ? '#fafafa' : null;
            },
            hLineColor: () => '#e0e0e0',
            vLineColor: () => '#e0e0e0',
          },
        },
      ],
      styles: {
        titleBar: {
          color: '#ffffff',
          fillColor: '#d68102',
          bold: true,
          fontSize: 13,
          margin: [8, 5, 8, 5],
        },
        summaryLabelCenter: {
          bold: true,
          fontSize: 8.5,
          alignment: 'center',
        },
        summaryValueCenter: {
          alignment: 'center',
          fontSize: 9,
          bold: true,
        },
        thLeft: {
          color: '#ffffff',
          bold: true,
          fontSize: 8.5,
          alignment: 'left',
        },
        thRight: {
          color: '#ffffff',
          bold: true,
          fontSize: 8.5,
          alignment: 'right',
        },
        tdLeft: {
          fontSize: 8.5,
          alignment: 'left',
        },
        tdRight: {
          fontSize: 8.5,
          alignment: 'right',
          noWrap: true,
        },
      },
    };

    const safeEmployee = String(selectedEmployeeName || 'zamestnanec')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    pdfMake.createPdf(docDefinition).download(`provize-${safeEmployee}-${selectedPeriod.year}-${String(selectedPeriod.month).padStart(2, '0')}.pdf`);
  };

  return (
    <div>
      <h2 className="page-header">Provize</h2>

      <div className="card">
        <div className="crm-toolbar crm-toolbar--provize-period">
          <select value={periodKey} onChange={(e) => setPeriodKey(e.target.value)}>
            <option value="previous">Minulý měsíc ({monthLabel(periodOptions.previous.month)} {periodOptions.previous.year})</option>
            <option value="current">Aktuální měsíc ({monthLabel(periodOptions.current.month)} {periodOptions.current.year})</option>
          </select>
          {canViewAll ? (
            <select value={selectedEmployeeId} onChange={(e) => setSelectedEmployeeId(e.target.value)}>
              {TECHNICI.map((item) => (
                <option key={item.id} value={item.id}>{item.fullName}</option>
              ))}
            </select>
          ) : (
            <input value={ownEmployee ? ownEmployee.fullName : 'Neznámý zaměstnanec'} disabled />
          )}
          <input
            value={`${monthLabel(selectedPeriod.month)} ${selectedPeriod.year}`}
            disabled
          />
          <button type="button" className="btn btn-blue" onClick={handleExportPdf}>
            Export PDF
          </button>
        </div>

        <div className="crm-summary-grid">
          <div className="crm-summary-card">
            <p>Provize zaměstnance</p>
            <strong>{formatMoney(summary.provizeZam)}</strong>
          </div>
          <div className="crm-summary-card">
            <p>Cena zakázek</p>
            <strong>{formatMoney(summary.cenaZakazky)}</strong>
          </div>
          <div className="crm-summary-card">
            <p>Doprava</p>
            <strong>{formatMoney(summary.doprava)}</strong>
          </div>
          <div className="crm-summary-card">
            <p>DPH</p>
            <strong>{formatMoney(summary.dph)}</strong>
          </div>
          <div className="crm-summary-card">
            <p>Daň</p>
            <strong>{formatMoney(summary.dan)}</strong>
          </div>
        </div>

        {error ? <p className="crm-error" style={{ marginTop: 15 }}>{error}</p> : null}
        {loading ? <p style={{ marginTop: 15 }}>Načítám...</p> : null}

        <Table
          headData={isMobile ? ['Provize'] : tableHead}
          renderHead={(item, index) => (
            <th
              key={index}
              className={isMobile ? undefined : columns[index].className}
              style={{
                textAlign: isMobile ? 'left' : (columns[index].numeric ? 'right' : 'left'),
                fontSize: '0.86rem',
                padding: '10px 8px',
                whiteSpace: isMobile ? 'normal' : (columns[index].numeric || index === 0 ? 'nowrap' : 'normal'),
              }}
            >
              {item}
            </th>
          )}
          bodyData={rows}
          limit={50}
          renderBody={(item, index) => (
            <tr key={`${item.ZakazkaId || 'no-id'}-${item.Datum || 'd'}-${item.Zakaznik || 'z'}-${index}`}>
              {isMobile ? (
                <td>
                  <div className="crm-mobile-stack">
                    <div className="crm-mobile-stack__header">
                      <div className="crm-mobile-stack__title-group">
                        <span className="crm-mobile-stack__eyebrow">{formatDate(item.Datum)}</span>
                        <span className="crm-mobile-stack__title" title={item.Zakaznik || ''}>{item.Zakaznik || '-'}</span>
                      </div>
                      <span className="crm-mobile-stack__value">{formatMoney(item.Provize_ZAM)}</span>
                    </div>
                    <span className="crm-mobile-stack__line">{item.Firma || item.Skudce || '-'}</span>
                    <div className="crm-mobile-stack__actions">
                      <span className="crm-mobile-stack__line">Cena {formatMoney(item.CenaZakazky)} • Doprava {formatMoney(item.CenaDoprava)}</span>
                      {item.ZakazkaId ? (
                        <Link
                          className="btn btn-blue table-action-btn table-action-btn--mobile"
                          to={{
                            pathname: `/zakazky/${item.ZakazkaId}`,
                            state: { returnTo: provizeReturnTo },
                          }}
                        >
                          Upravit
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </td>
              ) : (
                <>
                  <td className="provize-col-date" style={{ fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatDate(item.Datum)}</td>
                  <td className="provize-col-customer" style={{ fontSize: '0.86rem', padding: '10px 8px' }}>
                    <span className="crm-table-truncate" title={item.Zakaznik || ''}>{item.Zakaznik || '-'}</span>
                  </td>
                  <td className="provize-col-company provize-col-optional" style={{ fontSize: '0.86rem', padding: '10px 8px' }}>{item.Firma || '-'}</td>
                  <td className="provize-col-pest provize-col-optional" style={{ fontSize: '0.86rem', padding: '10px 8px' }}>{item.Skudce || '-'}</td>
                  <td className="provize-col-money" style={{ textAlign: 'right', fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatMoney(item.CenaZakazky)}</td>
                  <td className="provize-col-money provize-col-optional-small" style={{ textAlign: 'right', fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatMoney(item.CenaDoprava)}</td>
                  <td className="provize-col-money provize-col-optional" style={{ textAlign: 'right', fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatMoney(item.DPH)}</td>
                  <td className="provize-col-money provize-col-optional" style={{ textAlign: 'right', fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatMoney(item.Dan)}</td>
                  <td className="provize-col-money" style={{ textAlign: 'right', fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>{formatMoney(item.Provize_ZAM)}</td>
                  <td className="provize-col-actions" style={{ fontSize: '0.86rem', padding: '10px 8px', whiteSpace: 'nowrap' }}>
                    {item.ZakazkaId ? (
                      <Link
                        className="btn btn-blue table-action-btn table-action-btn--compact"
                        to={{
                          pathname: `/zakazky/${item.ZakazkaId}`,
                          state: { returnTo: provizeReturnTo },
                        }}
                      >
                        Upravit
                      </Link>
                    ) : '-'}
                  </td>
                </>
              )}
            </tr>
          )}
        />
      </div>
    </div>
  );
};

export default Provize;
